import React, { useEffect, useState } from 'react';

import { Modal, message } from 'antd';
import { T, TF } from 'react-translator-component';
import styled from 'styled-components';

import { applyScopeDelta, getScopeView } from '../../../../hooks/interoperability';
import { ScopeAccount } from '../../../../types/responses/Interoperability';
import { Pagination } from '../../../molecules/Pagination/Pagination';

const Wrapper = styled.div`
  background: #f7f8fa;
  border-top: 1px solid #e9ebee;
  padding: 14px 18px;
`;

const Caption = styled.div`
  font-weight: 600;
  font-size: 10.5px;
  line-height: 1;
  letter-spacing: 0.06em;
  color: #8a9099;
  text-transform: uppercase;
  margin-bottom: 10px;
`;

const Row = styled.div<{ indented: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
  padding-left: ${({ indented }) => (indented ? '14px' : '0')};
  font-size: 11.5px;
`;

const Mark = styled.span<{ $on: boolean }>`
  width: 14px;
  height: 14px;
  border-radius: 3px;
  border: 1.4px solid ${({ $on, theme }) => ($on ? theme.primary : '#c6cbd3')};
  background: ${({ $on, theme }) => ($on ? theme.primary : '#fff')};
  color: #fff;
  font-size: 10px;
  line-height: 12px;
  text-align: center;
  flex: none;
`;

const Tag = styled.span<{ root: boolean }>`
  background: ${({ root }) => (root ? '#eef4fe' : '#eef0f2')};
  color: ${({ root }) => (root ? '#1c4fae' : '#6b7280')};
  border-radius: 4px;
  padding: 1px 6px;
  font-size: 10px;
`;

const Stations = styled.span`
  color: #6b7280;
  margin-left: auto;
`;

const Mudanca = styled.span`
  color: #8a5300;
  font-size: 10px;
  margin-left: 6px;
`;

const Acoes = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 12px;
`;

const Salvar = styled.button`
  background: ${({ theme }) => theme.primary};
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 7px 14px;
  font-size: 11.5px;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Desfazer = styled.button`
  background: #fff;
  color: #4a5462;
  border: 1px solid #d9dce1;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 11.5px;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Contagem = styled.div`
  font-size: 10.5px;
  color: #6b7280;
  margin-top: 8px;
`;

const InheritanceNote = styled.p<{ inherits: boolean }>`
  margin: 10px 0 0;
  font-size: 11px;
  color: ${({ inherits }) => (inherits ? '#6b7280' : '#8a5300')};
`;

/**
 * Subcontas por pagina (MV2-1812).
 *
 * Dez, como pedido no refinamento do MV2-1709: a arvore inteira de uma rede grande
 * empurrava o resto do painel — as acoes de salvar e a nota de heranca — para fora da
 * tela, e a lista nao tem cabecalho fixo que ajude a se localizar no meio dela.
 */
const PAGE_SIZE = 10;

export type ScopeAccountsRowProps = {
  tenantId: string;
  partnerTenantId: string;
  /** Vem do contrato: muda o texto da nota de herança. */
  inheritsNewSubaccounts: boolean;

  /**
   * Deixa marcar e desmarcar conta, e salvar o novo escopo (MV2-1816).
   *
   * Só faz sentido em contrato em vigor: no revogado o servidor recusa, e
   * oferecer o controle prometeria um caminho que não existe. Fora da aba
   * Conexões a linha segue sendo leitura.
   */
  editable?: boolean;

  /** Avisa quem mostra os totais de que o escopo mudou. */
  onScopeChanged?: () => void;
};

/**
 * Linha expansível da aba Conexões (MV2-1713).
 *
 * Carrega sob demanda, ao expandir: buscar a hierarquia de todos os parceiros
 * junto com a listagem seria uma consulta por linha que quase ninguém abre.
 */
export const ScopeAccountsRow: React.FC<ScopeAccountsRowProps> = ({
  tenantId,
  partnerTenantId,
  inheritsNewSubaccounts,
  editable = false,
  onScopeChanged,
}) => {
  /** A matriz, fora da paginacao: vem em toda resposta e nunca sai do contrato. */
  const [raiz, setRaiz] = useState<ScopeAccount | null>(null);

  /** Apenas a pagina corrente de subcontas — o resto esta no servidor. */
  const [visiveis, setVisiveis] = useState<ScopeAccount[]>([]);
  const [totalSubcontas, setTotalSubcontas] = useState(0);

  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [pagina, setPagina] = useState(1);

  /**
   * O que a pessoa mexeu, e nao o escopo inteiro (MV2-1812).
   *
   * Com a arvore paginada a tela conhece so a pagina aberta, entao guardar "as
   * marcadas" seria guardar meia verdade: salvar mandaria esse conjunto parcial e
   * apagaria as contas das paginas que ninguem viu. O delta diz o que mudou; o
   * resto fica como o servidor tem.
   *
   * Guarda o nome junto do id porque a confirmacao nomeia quem sai, e a conta pode
   * ter sido marcada numa pagina que ja saiu da tela.
   */
  const [entrando, setEntrando] = useState<Map<string, string>>(new Map());
  const [saindo, setSaindo] = useState<Map<string, string>>(new Map());

  const carregar = async (paginaDesejada = pagina) => {
    try {
      setLoading(true);
      setFailed(false);

      const vista = await getScopeView(tenantId, partnerTenantId, paginaDesejada - 1, PAGE_SIZE);

      setRaiz(vista.root);
      setVisiveis(vista.content);
      setTotalSubcontas(vista.total_elements);

      // O servidor devolve a pagina valida mais proxima quando a pedida nao existe
      // mais; seguir a resposta evita paginador cheio com lista vazia.
      setPagina(vista.page + 1);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, partnerTenantId]);

  /**
   * A matriz nunca sai do contrato.
   *
   * Mesma regra da geração do convite, e o servidor a reaplica — travar aqui é
   * para o controle não prometer uma alteração que voltaria desfeita.
   */
  const travada = (account: ScopeAccount) => account.root;

  const alternar = (account: ScopeAccount) => {
    if (!editable || travada(account) || salvando) {
      return;
    }

    const id = account.tenant_id;

    // Desmarcar o que ja estava no contrato e uma SAIDA; marcar o que nao estava e
    // uma ENTRADA. Clicar duas vezes desfaz o proprio registro, e a conta volta a
    // nao aparecer no delta — nao vira "entrada" de algo que nunca saiu.
    if (account.in_contract) {
      setSaindo((atual) => alterna(atual, id, account.name));
    } else {
      setEntrando((atual) => alterna(atual, id, account.name));
    }
  };

  const alterna = (atual: Map<string, string>, id: string, nome: string) => {
    const proximo = new Map(atual);

    if (proximo.has(id)) proximo.delete(id);
    else proximo.set(id, nome);

    return proximo;
  };

  const sujo = entrando.size > 0 || saindo.size > 0;

  const descartar = () => {
    setEntrando(new Map());
    setSaindo(new Map());
    carregar();
  };

  const aplicar = async () => {
    try {
      setSalvando(true);
      await applyScopeDelta(tenantId, partnerTenantId, {
        entering: Array.from(entrando.keys()),
        leaving: Array.from(saindo.keys()),
      });
      message.success(T('Contract scope updated.'));
      setEntrando(new Map());
      setSaindo(new Map());
      await carregar();
      onScopeChanged?.();
    } catch (erro) {
      // A recusa do servidor e a util: contrato inativo, conta fora da hierarquia.
      const doServidor = (erro as { response?: { status?: number; data?: { message?: string } } })
        ?.response;

      message.error(
        ((doServidor?.status || 0) < 500 && doServidor?.data?.message)
          || T('Could not change the scope now. Nothing was changed.'),
      );
    } finally {
      setSalvando(false);
    }
  };

  /**
   * Confirma antes de salvar, nomeando quem sai.
   *
   * Tirar uma conta corta na hora o que o parceiro enxerga dela, e com a arvore
   * paginada a conta que sai pode nem estar na tela na hora do clique.
   *
   * O aviso fala das subcontas sem nomea-las (MV2-1847): a saida cascateia para a
   * subarvore de quem sai, e a tela conhece so a pagina aberta — listar nomes aqui
   * prometeria uma lista completa que ela nao tem como montar.
   */
  const confirmarEAplicar = () => {
    if (!sujo) {
      return;
    }

    Modal.confirm({
      title: T('Apply the new contract scope?'),
      content: saindo.size
        ? TF(
            'These accounts, and any sub-accounts below them, stop being displayed to the partner right away: {0}. The contract stays active.',
            Array.from(saindo.values()).join(', '),
          )
        : T('The marked accounts start being displayed to the partner. The contract stays active.'),
      okText: T('Apply'),
      cancelText: T('Cancel'),
      onOk: aplicar,
    });
  };

  const raizes = raiz ? [raiz] : [];
  const paginaAtual = pagina;

  /**
   * Onde a conta esta AGORA na tela: o que o servidor mandou, corrigido pelo delta
   * que ainda nao foi salvo.
   */
  const estaDentro = (account: ScopeAccount) => {
    if (!editable) {
      return account.in_contract;
    }

    if (saindo.has(account.tenant_id)) return false;
    if (entrando.has(account.tenant_id)) return true;

    return account.in_contract;
  };

  return (
    <Wrapper>
      <Caption>{T('Accounts of my hierarchy in this contract')}</Caption>

      {loading && <div>{T('Loading...')}</div>}
      {failed && <div>{T('Could not load the accounts of this contract.')}</div>}

      {!loading &&
        !failed &&
        [...raizes, ...visiveis].map((account) => {
          const dentro = estaDentro(account);
          const mudou = editable && dentro !== account.in_contract;

          return (
            <Row key={account.tenant_id} indented={!account.root}>
              {editable && !travada(account) ? (
                <input
                  type="checkbox"
                  checked={dentro}
                  disabled={salvando}
                  aria-label={account.name}
                  onChange={() => alternar(account)}
                />
              ) : (
                <Mark $on={dentro} title={travada(account) ? T('The parent account cannot leave the contract.') : undefined}>
                  {dentro ? '✓' : ''}
                </Mark>
              )}
              <strong>{account.name}</strong>
              <Tag root={account.root}>
                {account.root ? T('parent account') : T('sub-account')}
              </Tag>
              {mudou && <Mudanca>{dentro ? T('will be added') : T('will be removed')}</Mudanca>}
              <Stations>
                {account.station_count} {T('stations')}
              </Stations>
            </Row>
          );
        })}

      {/*
        O resumo do que esta pendente so aparece quando ha mais de uma pagina, porque
        e ai que a pergunta existe: a conta marcada numa pagina anterior some de vista,
        e e justamente ela que vai junto no salvar.

        Conta o DELTA, e nao "quantas estao no contrato": com a lista no servidor a
        tela nao conhece as outras paginas, e exibir um total calculado sobre a pagina
        visivel seria um numero errado com cara de certo.
      */}
      {!loading && !failed && totalSubcontas > PAGE_SIZE && (
        <>
          {sujo && (
            <Contagem>
              {TF('Pending changes: {0} entering, {1} leaving', entrando.size, saindo.size)}
            </Contagem>
          )}

          {/* Paginador do dashboard, 1-based como ele espera. */}
          <Pagination
            currentPage={paginaAtual}
            pageSize={PAGE_SIZE}
            totalCount={totalSubcontas}
            onPageChange={(proxima: number) => carregar(proxima)}
          />
        </>
      )}

      {editable && !loading && !failed && (
        <Acoes>
          <Salvar type="button" disabled={!sujo || salvando} onClick={confirmarEAplicar}>
            {salvando ? T('Applying...') : T('Apply scope')}
          </Salvar>
          <Desfazer type="button" disabled={!sujo || salvando} onClick={descartar}>
            {T('Undo')}
          </Desfazer>
        </Acoes>
      )}

      {!loading && !failed && (
        <InheritanceNote inherits={inheritsNewSubaccounts}>
          {inheritsNewSubaccounts
            ? T('New sub-accounts join this contract automatically — no new invite.')
            : T('New sub-accounts do NOT join automatically: each inclusion is an explicit action.')}
        </InheritanceNote>
      )}
    </Wrapper>
  );
};
